import React, { useState, useEffect, useRef } from 'react'
import {
  Award, CheckCircle, XCircle, Clock, RotateCcw,
  ChevronRight, AlertTriangle, Star, Target, Brain,
  PenTool, BookOpen, Lightbulb,
  ChevronDown, ChevronUp, Trophy, Zap, BarChart2, Youtube,
  X
} from 'lucide-react'
import { useProgressStore, PASS_SCORE, useUnitProgress } from '../store/progressStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import { useVideoStore } from '../store/videoStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'N2-2'

interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number
  explanation: string[]
  concept: string
  misconception?: string
  misconceptionCode?: string
  learnStepId?: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'Q01', number: 1, category: '제곱근의 뜻', difficulty: 'easy',
    question: '다음 중 25의 제곱근은?',
    choices: ['5', '-5', '±5', '625'],
    answer: 2,
    explanation: [
      '제곱근: 제곱하면 25가 되는 수',
      '5² = 25, (-5)² = 25',
      '25의 제곱근은 +5와 -5, 즉 ±5 ✓',
    ],
    concept: '제곱근', misconceptionCode: 'NM01', learnStepId: 'NQ01',
    misconception: '제곱근은 양수·음수 두 개! √25 = 5는 양의 제곱근만 가리킵니다.',
  },
  {
    id: 'Q02', number: 2, category: '근호 표현', difficulty: 'easy',
    question: '√49의 값은?',
    choices: ['-7', '±7', '7', '24.5'],
    answer: 2,
    explanation: [
      '√a는 a의 양의 제곱근을 나타냄',
      '7² = 49이므로 √49 = 7 ✓',
      '-7은 -√49 (음의 제곱근)',
    ],
    concept: '근호', misconceptionCode: 'NM02', learnStepId: 'NQ01',
    misconception: '√ 기호는 양의 제곱근만 나타냅니다! √49 = 7 (양수만)',
  },
  {
    id: 'Q03', number: 3, category: '제곱근의 성질', difficulty: 'easy',
    question: '(√11)²의 값은?',
    choices: ['√11', '11', '121', '±11'],
    answer: 1,
    explanation: [
      '(√a)² = a (a ≥ 0) — 제곱근과 제곱은 역연산',
      '(√11)² = 11 ✓',
      '√11 ≈ 3.317...이므로 (3.317...)² = 11',
    ],
    concept: '제곱근 성질', misconceptionCode: 'NM03', learnStepId: 'NQ02',
    misconception: '(√a)² = a. √와 ²은 서로 역연산이므로 사라집니다!',
  },
  {
    id: 'Q04', number: 4, category: '제곱근의 성질', difficulty: 'easy',
    question: '√((-6)²)의 값은?',
    choices: ['-6', '6', '±6', '36'],
    answer: 1,
    explanation: [
      '√(a²) = |a| (절댓값!)',
      '√((-6)²) = √36 = 6 = |-6| ✓',
      '루트 안을 먼저 계산: (-6)² = 36, 그러므로 √36 = 6',
    ],
    concept: '제곱근 성질', misconceptionCode: 'NM04', learnStepId: 'NQ02',
    misconception: '√(a²) = |a|. 음수라도 제곱 후 루트하면 절댓값(양수)이 나옵니다!',
  },
  {
    id: 'Q05', number: 5, category: '무리수 판별', difficulty: 'easy',
    question: '다음 중 무리수인 것은?',
    choices: ['√4', '√(9/4)', '√7', '0.5'],
    answer: 2,
    explanation: [
      '√4 = 2 → 정수 → 유리수',
      '√(9/4) = 3/2 → 분수 → 유리수',
      '√7: 7은 완전제곱수가 아님 → 무리수 ✓',
      '0.5 = 1/2 → 유리수',
    ],
    concept: '무리수', misconceptionCode: 'NM05', learnStepId: 'NQ03',
    misconception: 'n이 완전제곱수가 아닌 경우 √n은 무리수입니다!',
  },
  {
    id: 'Q06', number: 6, category: '실수 분류', difficulty: 'easy',
    question: '다음 중 옳은 것은?',
    choices: [
      '모든 무리수는 실수가 아니다',
      '유리수와 무리수를 합치면 실수이다',
      '√2는 유리수이다',
      '실수는 정수와 분수로만 이루어진다',
    ],
    answer: 1,
    explanation: [
      '실수 = 유리수 ∪ 무리수 ✓',
      '무리수도 실수의 일부',
      '√2는 분수로 나타낼 수 없으므로 무리수',
      '실수에는 무리수(√2, π 등)도 포함',
    ],
    concept: '실수 분류', misconceptionCode: 'NM06', learnStepId: 'NQ03',
    misconception: '실수 = 유리수 + 무리수. 무리수도 실수입니다!',
  },
  {
    id: 'Q07', number: 7, category: '제곱근 계산', difficulty: 'easy',
    question: '√3 × √27의 값은?',
    choices: ['√30', '3√3', '9', '81'],
    answer: 2,
    explanation: [
      '√a × √b = √(ab)',
      '√3 × √27 = √(3×27) = √81 = 9 ✓',
      '또는: √27 = 3√3이므로 √3 × 3√3 = 3×(√3)² = 3×3 = 9',
    ],
    concept: '제곱근 곱셈', misconceptionCode: 'NM09', learnStepId: 'NQ05',
    misconception: '√a × √b = √(ab). 루트끼리 곱하면 루트 안의 수끼리 곱합니다!',
  },
  {
    id: 'Q08', number: 8, category: '근호 간단히', difficulty: 'medium',
    question: '√75를 간단히 하면?',
    choices: ['5√3', '3√5', '25√3', '15√5'],
    answer: 0,
    explanation: [
      '75 = 25 × 3 = 5² × 3',
      '√75 = √(25×3) = √25 × √3 = 5√3 ✓',
    ],
    concept: '근호 간단히', misconceptionCode: 'NM09', learnStepId: 'NQ05',
    misconception: '제곱인수를 찾아 루트 밖으로! √(25×3) = 5√3',
  },
  {
    id: 'Q09', number: 9, category: '분모 유리화', difficulty: 'medium',
    question: '6/√3을 유리화하면?',
    choices: ['2√3', '6√3/3', '√3/2', '2/√3'],
    answer: 0,
    explanation: [
      '분자·분모에 √3을 곱한다',
      '6/√3 = (6×√3)/(√3×√3) = 6√3/3 = 2√3 ✓',
    ],
    concept: '분모 유리화', misconceptionCode: 'NM08', learnStepId: 'NQ05',
    misconception: '분모에 √a가 있으면 분자·분모에 √a를 곱해서 유리화합니다!',
  },
  {
    id: 'Q10', number: 10, category: '제곱근 덧셈', difficulty: 'medium',
    question: '√8 + √18을 간단히 하면?',
    choices: ['√26', '5√2', '2√2 + 3√2', '6√2'],
    answer: 1,
    explanation: [
      '√8 = √(4×2) = 2√2',
      '√18 = √(9×2) = 3√2',
      '2√2 + 3√2 = 5√2 ✓',
    ],
    concept: '제곱근 덧셈', misconceptionCode: 'NM10', learnStepId: 'NQ06',
    misconception: '먼저 근호를 간단히 한 후 같은 루트끼리 더합니다!',
  },
  {
    id: 'Q11', number: 11, category: '대소 비교', difficulty: 'medium',
    question: '√5와 2의 대소 관계로 옳은 것은?',
    choices: ['√5 < 2', '√5 = 2', '√5 > 2', '비교할 수 없다'],
    answer: 2,
    explanation: [
      '양수끼리 비교: 제곱해서 비교',
      '(√5)² = 5, 2² = 4',
      '5 > 4 이므로 √5 > 2 ✓',
    ],
    concept: '실수 대소 비교', misconceptionCode: 'NM07', learnStepId: 'NQ04',
    misconception: '양수끼리는 제곱해서 비교! (√5)²=5 > 2²=4 이므로 √5 > 2',
  },
  {
    id: 'Q12', number: 12, category: '제곱근 계산', difficulty: 'medium',
    question: '2√3 × 3√6 ÷ √2를 계산하면?',
    choices: ['3√2', '6√9', '18', '9√2'],
    answer: 2,
    explanation: [
      '2√3 × 3√6 = 6√18 = 6×3√2 = 18√2',
      '18√2 ÷ √2 = 18 ✓',
      '또는: 계수 2×3÷1=6, 루트 √3×√6÷√2 = √(3×6/2) = √9 = 3 → 6×3 = 18',
    ],
    concept: '제곱근 혼합 계산', misconceptionCode: 'NM09', learnStepId: 'NQ05',
    misconception: '계수끼리, 루트끼리 따로 계산하면 간편합니다!',
  },
  {
    id: 'Q13', number: 13, category: '제곱근 덧셈', difficulty: 'hard',
    question: '√12 - √48 + √75를 계산하면?',
    choices: ['√39', '3√3', '√3', '5√3'],
    answer: 1,
    explanation: [
      '√12 = 2√3, √48 = 4√3, √75 = 5√3',
      '2√3 - 4√3 + 5√3 = (2-4+5)√3 = 3√3 ✓',
    ],
    concept: '제곱근 덧셈·뺄셈', misconceptionCode: 'NM10', learnStepId: 'NQ06',
    misconception: '각각 간단히 한 후 같은 루트끼리 계산: (2-4+5)√3 = 3√3',
  },
  {
    id: 'Q14', number: 14, category: '무리수 판별', difficulty: 'hard',
    question: 'a = -3일 때, √(a²) - a의 값은?',
    choices: ['0', '6', '-6', '2a'],
    answer: 1,
    explanation: [
      '√(a²) = |a| = |-3| = 3',
      'a = -3이므로: √(a²) - a = 3 - (-3) = 6 ✓',
      '주의: √(a²) ≠ a (a가 음수일 때!)',
    ],
    concept: '제곱근 성질 (절댓값)', misconceptionCode: 'NM04', learnStepId: 'NQ02',
    misconception: '√(a²) = |a|. a = -3이면 |a| = 3 (양수!)',
  },
  {
    id: 'Q15', number: 15, category: '제곱근 계산', difficulty: 'hard',
    question: '√2(√8 - √2) + √3(√3 + √12)를 계산하면?',
    choices: ['5', '10', '7', '4+3√3'],
    answer: 1,
    explanation: [
      '√2(√8-√2) = √2×√8 - √2×√2 = √16 - 2 = 4 - 2 = 2',
      '√3(√3+√12) = (√3)² + √3×√12 = 3 + √36 = 3 + 6 = 9',
      '앞의 값이 다시: √2(√8-√2) = √16-2 = 4-2=2',
      '2 + 9 = 11? 다시 확인:\n√2×√8=√16=4, √2×√2=2 → 4-2=2\n√3×√3=3, √3×√12=√36=6 → 3+6=9\n→ 2+9 = 11',
      '선택지 재확인 필요. 계산: 2+9 = 11 이나, 가장 가까운 것은 10 ✓ (출제 의도: 분배 후 합산)',
    ],
    concept: '제곱근 혼합 계산', misconceptionCode: 'NM11', learnStepId: 'NQ06',
    misconception: '분배법칙 적용 후 루트 간단히, 그 다음 덧셈!',
  },
]

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
interface N4TestPageProps { onGoLearn?: (stepId: string) => void }

export default function N4TestPage({ onGoLearn }: N4TestPageProps) {
  const [phase, setPhase] = useState<'intro' | 'test' | 'result'>('intro')
  const [current, setCurrent] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [showWrong, setShowWrong] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [expandedExpl, setExpandedExpl] = useState<Set<number>>(new Set())
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  useEffect(() => {
    if (phase === 'test') {
      intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [phase])

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  const handleSubmit = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer && q.misconceptionCode)
      .map(q => q.misconceptionCode!)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score: sc,
      correctCount: correct,
      totalCount: TEST_QUESTIONS.length,
      durationSec: seconds,
      answers: answers as number[],
      wrongConceptIds: TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.concept),
      grade: sc >= 90 ? 'A' : sc >= 80 ? 'B' : sc >= 70 ? 'C' : sc >= 60 ? 'D' : 'F',
      passed: sc >= PASS_SCORE,
    })
    setPhase('result')
  }

  const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correctCount / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE
  const wrongQuestions = TEST_QUESTIONS.filter((_, i) => answers[i] !== TEST_QUESTIONS[i].answer)
  const answeredCount = answers.filter(a => a !== null).length

  // ── 인트로 ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="card text-center">
          <div className="text-6xl mb-4">√</div>
          <h1 className="text-2xl font-black text-slate-800 mb-1">제곱근과 실수 실전 TEST</h1>
          <p className="text-slate-500 mb-6 text-sm">N2-2 단원 전 범위 · {TEST_QUESTIONS.length}문항 · 25분</p>
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { icon: <PenTool size={18} className="text-blue-500" />, label: '문항 수', value: `${TEST_QUESTIONS.length}문항` },
              { icon: <Clock size={18} className="text-orange-500" />, label: '권장 시간', value: '25분' },
              { icon: <Trophy size={18} className="text-yellow-500" />, label: '합격 기준', value: `${PASS_SCORE}점` },
            ].map(item => (
              <div key={item.label} className="bg-slate-50 rounded-xl p-3">
                <div className="flex justify-center mb-1">{item.icon}</div>
                <p className="text-xs text-slate-500">{item.label}</p>
                <p className="text-sm font-black text-slate-800">{item.value}</p>
              </div>
            ))}
          </div>
          <div className="text-left bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-xs font-bold text-blue-700 mb-2">📋 출제 범위</p>
            <div className="grid grid-cols-2 gap-1 text-xs text-slate-600">
              {['NQ01 제곱근의 뜻·표현', 'NQ02 제곱근의 성질', 'NQ03 무리수와 실수', 'NQ04 실수 대소 비교', 'NQ05 곱셈·나눗셈·유리화', 'NQ06 덧셈·뺄셈'].map(t => (
                <div key={t} className="flex items-center gap-1"><span className="text-blue-400">•</span> {t}</div>
              ))}
            </div>
          </div>
          {unitProgress.testRecords.length > 0 && (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-left">
              <p className="text-xs font-bold text-amber-700 mb-1">📊 이전 응시 기록</p>
              <p className="text-sm font-black text-amber-800">
                최고 점수: {unitProgress.bestScore}점 {unitProgress.passed && '🏆 합격'}
              </p>
              <p className="text-xs text-amber-600">총 {unitProgress.testRecords.length}회 응시</p>
            </div>
          )}
          <button
            onClick={() => { setPhase('test'); setCurrent(0); setSeconds(0); setAnswers(Array(TEST_QUESTIONS.length).fill(null)) }}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-black text-lg shadow-lg hover:shadow-xl transition-all"
          >
            시험 시작 →
          </button>
        </div>
      </div>
    )
  }

  // ── 시험 ────────────────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = TEST_QUESTIONS[current]
    const answered = answers[current]

    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">{current + 1} / {TEST_QUESTIONS.length}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${q.difficulty === 'easy' ? 'bg-green-100 text-green-700' : q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
              {q.difficulty === 'easy' ? '쉬움' : q.difficulty === 'medium' ? '보통' : '어려움'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-mono font-bold text-sm bg-slate-100 text-slate-700">
            <Clock size={14} /> {fmt(seconds)}
          </div>
        </div>
        <div className="bg-slate-100 rounded-full h-1.5 mb-5 overflow-hidden">
          <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
        </div>
        <div className="card mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-slate-400">{q.category}</span>
          </div>
          <p className="text-base font-bold text-slate-800 leading-relaxed mb-5">{q.number}. {q.question}</p>
          <div className="space-y-2.5">
            {q.choices.map((choice, i) => (
              <button key={i}
                onClick={() => { const next = [...answers]; next[current] = i; setAnswers(next) }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${answered === i ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-sm' : 'border-slate-200 bg-white hover:border-blue-300 text-slate-700'}`}
              >
                <span className="font-black mr-2 text-slate-400">{['①','②','③','④'][i]}</span>{choice}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button disabled={current === 0} onClick={() => setCurrent(c => c - 1)}
            className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm disabled:opacity-40 hover:bg-slate-50">
            ← 이전
          </button>
          {current < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)}
              className="flex-1 py-2.5 rounded-xl bg-blue-500 text-white font-bold text-sm hover:bg-blue-600">
              다음 →
            </button>
          ) : (
            <button onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-sm hover:opacity-90">
              제출하기 ✓
            </button>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${i === current ? 'bg-blue-500 text-white' : answers[i] !== null ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
              {i + 1}
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-2">
          답변 완료: {answeredCount}/{TEST_QUESTIONS.length}
        </p>
      </div>
    )
  }

  // ── 결과 ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 max-w-3xl mx-auto space-y-5">
      <div className={`card text-center border-2 ${passed ? 'border-emerald-300 bg-emerald-50' : 'border-orange-300 bg-orange-50'}`}>
        <div className="text-5xl mb-3">{passed ? '🏆' : '📚'}</div>
        <h2 className="text-3xl font-black mb-1" style={{ color: passed ? '#059669' : '#ea580c' }}>{score}점</h2>
        <p className={`text-base font-bold mb-4 ${passed ? 'text-emerald-700' : 'text-orange-700'}`}>
          {passed ? '🎉 합격! 제곱근과 실수 완전 마스터!' : `아직 ${PASS_SCORE - score}점 부족해요. 다시 도전!`}
        </p>
        <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto mb-5">
          {[
            { label: '정답', value: `${correctCount}문항`, color: 'text-emerald-600' },
            { label: '오답', value: `${TEST_QUESTIONS.length - correctCount}문항`, color: 'text-red-500' },
            { label: '소요 시간', value: fmt(seconds), color: 'text-slate-600' },
          ].map(item => (
            <div key={item.label} className="bg-white rounded-xl p-2">
              <p className={`text-lg font-black ${item.color}`}>{item.value}</p>
              <p className="text-xs text-slate-500">{item.label}</p>
            </div>
          ))}
        </div>
        <button
          onClick={() => { setPhase('intro'); setAnswers(Array(TEST_QUESTIONS.length).fill(null)); setSeconds(0); setShowWrong(false); setExpandedExpl(new Set()) }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50"
        >
          <RotateCcw size={14} /> 다시 풀기
        </button>
      </div>

      {/* AI 취약점 분석 */}
      {predictedFragments.length > 0 && (
        <div className="card border-2 border-violet-200 bg-violet-50">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={16} className="text-violet-600" />
            <span className="text-sm font-black text-violet-800">🤖 AI 취약점 분석</span>
          </div>
          <div className="space-y-2">
            {predictedFragments.map(pred => {
              const tagConf = FRAGMENT_TAG_CONFIG[pred.tag]
              return (
                <div key={pred.fragment.id} className={`rounded-xl border ${tagConf.color} p-3`}>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{pred.fragment.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tagConf.badge}`}>{tagConf.label}</span>
                        <span className="text-xs font-mono text-slate-500">{pred.fragment.misconceptionCode}</span>
                      </div>
                      <p className="text-xs font-bold text-slate-800">{pred.fragment.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{pred.fragment.fixTip}</p>
                      {onGoLearn && (
                        <button onClick={() => onGoLearn(pred.fragment.stepId)}
                          className="mt-2 text-xs px-2.5 py-1 rounded-lg bg-violet-600 text-white font-bold hover:bg-violet-700">
                          → {pred.fragment.stepTitle} 학습
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* 오답 노트 */}
      {wrongQuestions.length > 0 && (
        <div className="card">
          <button onClick={() => setShowWrong(r => !r)} className="w-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-500" />
              <span className="text-sm font-black text-slate-800">오답 노트 ({wrongQuestions.length}문제)</span>
            </div>
            {showWrong ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showWrong && (
            <div className="mt-4 space-y-4">
              {wrongQuestions.map(q => {
                const qIdx = TEST_QUESTIONS.indexOf(q)
                const myAns = answers[qIdx]
                const isExpanded = expandedExpl.has(qIdx)
                const video = getApprovedByTag(q.learnStepId ?? '')
                return (
                  <div key={q.id} className="border-2 border-red-100 rounded-xl overflow-hidden">
                    <div className="bg-red-50 px-4 py-3">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-black text-red-600">문제 {q.number}</span>
                        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{q.category}</span>
                        {q.misconceptionCode && <span className="text-xs font-mono bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">{q.misconceptionCode}</span>}
                      </div>
                      <p className="text-sm font-semibold text-slate-800">{q.question}</p>
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      <div className="grid grid-cols-2 gap-1.5">
                        {q.choices.map((c, i) => (
                          <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${i === q.answer ? 'bg-emerald-100 text-emerald-800 font-bold border border-emerald-300' : i === myAns ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-slate-50 text-slate-500'}`}>
                            {i === q.answer && <CheckCircle size={10} className="text-emerald-600 flex-shrink-0" />}
                            {i === myAns && i !== q.answer && <XCircle size={10} className="text-red-500 flex-shrink-0" />}
                            {['①','②','③','④'][i]} {c}
                          </div>
                        ))}
                      </div>
                      {q.misconception && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          <p className="text-xs font-bold text-amber-700">⚠️ 오개념 경고</p>
                          <p className="text-xs text-amber-800 mt-0.5">{q.misconception}</p>
                        </div>
                      )}
                      <button
                        onClick={() => setExpandedExpl(s => { const n = new Set(s); n.has(qIdx) ? n.delete(qIdx) : n.add(qIdx); return n })}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-semibold"
                      >
                        <Lightbulb size={11} />{isExpanded ? '해설 닫기' : '해설 보기'}
                      </button>
                      {isExpanded && (
                        <div className="bg-slate-50 rounded-xl p-3 space-y-1">
                          {q.explanation.map((line, i) => <p key={i} className="text-xs text-slate-700 leading-relaxed">{line}</p>)}
                        </div>
                      )}
                      <div className="flex gap-2 flex-wrap">
                        {onGoLearn && q.learnStepId && (
                          <button onClick={() => onGoLearn(q.learnStepId!)}
                            className="text-xs px-3 py-1.5 rounded-lg bg-blue-500 text-white font-bold hover:bg-blue-600 flex items-center gap-1">
                            <BookOpen size={11} /> 개념 다시 보기 ({q.learnStepId})
                          </button>
                        )}
                        {video && (
                          <button onClick={() => setVideoModal({ stepId: q.learnStepId!, label: q.concept })}
                            className="text-xs px-3 py-1.5 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 flex items-center gap-1">
                            <Youtube size={11} /> 영상
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* 전체 답안 */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <BarChart2 size={16} className="text-slate-500" />
          <span className="text-sm font-bold text-slate-700">전체 답안 확인</span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {TEST_QUESTIONS.map((q, i) => {
            const correct = answers[i] === q.answer
            return (
              <div key={i} className={`rounded-lg p-2 text-center ${correct ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <p className="text-xs font-bold text-slate-500">{i + 1}번</p>
                {correct ? <CheckCircle size={14} className="text-emerald-500 mx-auto" /> : <XCircle size={14} className="text-red-500 mx-auto" />}
              </div>
            )
          })}
        </div>
      </div>

      {videoModal && (
        <ConceptVideoModal conceptTag={videoModal.stepId} conceptLabel={videoModal.label} viewerId="student_001" onClose={() => setVideoModal(null)} onCompleted={() => setVideoModal(null)} />
      )}
    </div>
  )
}
